/**
 * The voice cap (04 §5): at most `MAX_VOICES` effect voices sound at once. When a new one needs
 * room, the least important goes, and among equals the oldest. A newcomer less important than
 * everything playing is simply not played.
 *
 * What is counted is voices sounding *together*. A jingle hands over all its notes at once, each
 * to start later; a note holds a place only while it sounds, so a twenty-note phrase costs the
 * two or three places its lines need and never crowds itself out. Pure.
 */

export const MAX_VOICES = 10;

export interface Slot {
  /** Audio-clock seconds the voice starts and falls silent. */
  readonly start: number;
  readonly end: number;
  /** Higher is more important (see `PRIORITY` in scan.ts). */
  readonly priority: number;
}

export type Admission =
  | { readonly kind: 'room' }
  | { readonly kind: 'steal'; readonly index: number }
  | { readonly kind: 'drop' };

/** The voices not yet silent at `now` (those still to start among them). */
export function expire<T extends Slot>(slots: readonly T[], now: number): T[] {
  return slots.filter((s) => s.end > now);
}

/** Indices of the voices sounding at `t`. */
function soundingAt(slots: readonly Slot[], t: number): number[] {
  const on: number[] = [];
  slots.forEach((s, i) => {
    if (s.start <= t && s.end > t) on.push(i);
  });
  return on;
}

/** The voices sounding at the busiest moment of `v`'s life: its own start, or another's start within it. */
function crowd(slots: readonly Slot[], v: Slot): number[] {
  let most = soundingAt(slots, v.start);
  for (const s of slots) {
    if (s.start <= v.start || s.start >= v.end) continue;
    const on = soundingAt(slots, s.start);
    if (on.length > most.length) most = on;
  }
  return most;
}

/** Whether the new voice `v` fits, and if not, which voice to steal for it. */
export function admit(slots: readonly Slot[], v: Slot, cap = MAX_VOICES): Admission {
  const busy = crowd(slots, v);
  if (busy.length < cap) return { kind: 'room' };
  let victim = -1;
  for (const i of busy) {
    const s = slots[i];
    const best = victim >= 0 ? slots[victim] : undefined;
    if (!s) continue;
    if (!best || s.priority < best.priority || (s.priority === best.priority && s.start < best.start)) victim = i;
  }
  const lost = slots[victim];
  if (!lost || lost.priority > v.priority) return { kind: 'drop' };
  return { kind: 'steal', index: victim };
}
